import {
  Alert,
  Badge,
  Button,
  Chart,
  formatDate,
  Money,
  SettingsSection,
  SkeletonText,
  Stack,
  StatCard,
  StatStrip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from '@hkdevs/design-system';
import { useOperatorDashboard, type OperatorDashboard } from '../../api/queries/operator';
import { PageLayout } from '../../shell/Page';
import { dollars } from '../lineItems/LineItemList';

type Dashboard = OperatorDashboard;
type Providers = Dashboard['signIns']['lastDay'];
type Change = Dashboard['sensitiveChanges'][number];

const count = (n: number) => n.toLocaleString('en-US');

/** A duration in its largest whole unit: 45 s, 4 min, 3 h, 2 d. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h`;
  return `${Math.floor(seconds / 86_400)} d`;
}

const when = (instant: string) => formatDate(instant, 'uploaded');

// The hour on a 24-hour clock, so 24 labels fit under the bars: "13".
const hourLabel = (instant: string) =>
  new Date(instant).toLocaleTimeString('en-US', { hour: '2-digit', hourCycle: 'h23' });

/** The running version (pc-48h): the image's commit, its build time, the process's start. */
function Version({ version }: Pick<Dashboard, 'version'>) {
  return (
    <SettingsSection title="Version" description="The API that answered this page.">
      <StatStrip>
        <StatCard
          label="Commit"
          value={version.commit ? <Text mono>{version.commit}</Text> : 'unknown'}
          hint={version.commit ? undefined : 'A local or dev build'}
        />
        <StatCard
          label="Built"
          value={version.buildTime ? when(version.buildTime) : 'unknown'}
          hint="The commit’s time"
        />
        <StatCard label="Started" value={when(version.startedAt)} hint="This API process" />
      </StatStrip>
    </SettingsSection>
  );
}

/** Recorded errors (pc-m6x): API 5xx per hour, and the latest with their request IDs. */
function Errors({ errors }: Pick<Dashboard, 'errors'>) {
  return (
    <SettingsSection
      title="Errors"
      description="API 5xx responses and worker job failures, kept 30 days. Find one in the logs by its request ID."
    >
      <Stack gap={3}>
        {errors.droppedLastDay > 0 && (
          <Alert tone="warning" title="Some errors weren’t recorded">
            {count(errors.droppedLastDay)} errors in the last 24 hours went over the limit of about
            60 a minute. The logs have them all.
          </Alert>
        )}
        <StatStrip>
          <StatCard
            label="API errors"
            value={count(errors.apiLastDay)}
            hint="Last 24 hours"
            tone={errors.apiLastDay > 0 ? 'danger' : 'neutral'}
          />
          <StatCard
            label="Job failures"
            value={count(errors.workerLastDay)}
            hint="Last 24 hours"
            tone={errors.workerLastDay > 0 ? 'danger' : 'neutral'}
          />
        </StatStrip>
        <Chart
          type="bar"
          title="API errors per hour"
          categories={errors.hourly.map((hour) => hourLabel(hour.hour))}
          series={[{ name: 'Errors', data: errors.hourly.map((hour) => hour.count) }]}
          valueFormatter={(value) => count(value)}
        />
        {errors.latest.length === 0 ? (
          <Text tone="muted">No errors recorded.</Text>
        ) : (
          <Table aria-label="Latest errors" caption="Latest errors">
            <TableHead>
              <TableCell>When</TableCell>
              <TableCell>Where</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Request ID</TableCell>
            </TableHead>
            <TableBody>
              {errors.latest.map((error) => (
                <TableRow key={error.id}>
                  <TableCell label="When">{when(error.at)}</TableCell>
                  <TableCell label="Where" tone="mono">
                    {error.source === 'worker'
                      ? `worker: ${error.jobKind ?? 'job'}`
                      : `${error.status ?? ''} ${error.method ?? ''} ${error.path ?? '(no route)'}`}
                  </TableCell>
                  <TableCell label="Error">
                    {[error.exceptionType, error.message].filter(Boolean).join(': ') || '—'}
                    {error.user ? ` (${error.user.name})` : ''}
                  </TableCell>
                  <TableCell label="Request ID" tone="mono">
                    {error.requestId ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </SettingsSection>
  );
}

function Worker({ worker, jobs }: Pick<Dashboard, 'worker' | 'jobs'>) {
  return (
    <SettingsSection
      title="Worker"
      description="The background worker processes uploads and exports. It checks in every 10 seconds."
    >
      <Stack gap={3}>
        {worker.stale && (
          <Alert tone="danger" title="The worker isn’t checking in">
            {worker.lastSeenAt
              ? `Its last heartbeat was ${duration(worker.ageSeconds)} ago (${when(worker.lastSeenAt)}).`
              : 'There’s no heartbeat at all.'}{' '}
            Uploads and exports wait until it’s back. Check the worker’s logs.
          </Alert>
        )}
        <StatStrip>
          <StatCard
            label="Last heartbeat"
            value={worker.lastSeenAt ? `${duration(worker.ageSeconds)} ago` : 'Never'}
            hint={worker.stale ? 'Stale' : `Stale after ${duration(worker.staleAfterSeconds)}`}
            tone={worker.stale ? 'danger' : 'neutral'}
          />
          <StatCard
            label="Oldest waiting job"
            value={duration(jobs.oldestQueuedAgeSeconds)}
            hint={jobs.oldestQueuedAt ? `Due ${when(jobs.oldestQueuedAt)}` : 'Nothing waiting'}
          />
        </StatStrip>
      </Stack>
    </SettingsSection>
  );
}

function Jobs({ jobs }: Pick<Dashboard, 'jobs'>) {
  return (
    <SettingsSection
      title="Job queue"
      description="Background jobs now, and the last day’s results."
    >
      <Stack gap={3}>
        <StatStrip>
          <StatCard
            label="Queued"
            value={count(jobs.queued)}
            hint={`${count(jobs.retrying)} retrying`}
          />
          <StatCard label="Running" value={count(jobs.running)} />
          <StatCard label="Succeeded" value={count(jobs.succeededLastDay)} hint="Last 24 hours" />
          <StatCard
            label="Failed"
            value={count(jobs.failedLastDay)}
            hint="Last 24 hours"
            tone={jobs.failedLastDay > 0 ? 'danger' : 'neutral'}
          />
        </StatStrip>
        {jobs.recentFailures.length === 0 ? (
          <Text tone="muted">No failed attempts in the last 7 days.</Text>
        ) : (
          <Table aria-label="Recent job failures" caption="Recent failures (7 days)">
            <TableHead>
              <TableCell>Job</TableCell>
              <TableCell>When</TableCell>
              <TableCell numeric>Attempts</TableCell>
              <TableCell>Error</TableCell>
            </TableHead>
            <TableBody>
              {jobs.recentFailures.map((failure) => (
                <TableRow key={failure.id}>
                  <TableCell label="Job" tone="mono">
                    {failure.kind}{' '}
                    {failure.retrying ? (
                      <Badge tone="warning" size="sm">
                        Retrying
                      </Badge>
                    ) : (
                      <Badge tone="danger" size="sm">
                        Failed
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell label="When">{when(failure.at)}</TableCell>
                  <TableCell label="Attempts" numeric>
                    {failure.attempts} of {failure.maxAttempts}
                  </TableCell>
                  <TableCell label="Error">{failure.error}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </SettingsSection>
  );
}

function Uploads({ uploads }: Pick<Dashboard, 'uploads'>) {
  const { counts, pending, lastImport, exportsLastWeek: exports } = uploads;
  return (
    <SettingsSection
      title="Uploads"
      description="Pay data uploads, the pending pay set, and exports."
    >
      <Stack gap={3}>
        <StatStrip>
          <StatCard
            label="Waiting"
            value={count(counts.queued + counts.processing)}
            hint={`${count(counts.processing)} processing`}
          />
          <StatCard label="Completed" value={count(counts.completed)} hint="All time" />
          <StatCard
            label="Rejected"
            value={count(counts.rejected)}
            hint="All time"
            tone={counts.rejected > 0 ? 'danger' : 'neutral'}
          />
          <StatCard
            label="Queued to done"
            value={duration(uploads.medianSecondsLastWeek)}
            hint={`Median, 7 days; longest ${duration(uploads.maxSecondsLastWeek)}`}
          />
          <StatCard
            label="Pending pay"
            value={<Money value={dollars(pending.totalCents)} />}
            hint={pending.payDate ? `For ${formatDate(pending.payDate)}` : 'No pending date'}
          />
          <StatCard
            label="Exports"
            value={count(exports.completed)}
            hint={`7 days; ${count(exports.queued)} queued, ${count(exports.failed)} failed`}
          />
        </StatStrip>
        <Text>
          {lastImport
            ? `Last import: ${lastImport.fileName}, ${when(lastImport.finishedAt)}${
                lastImport.records === null ? '' : `, ${count(lastImport.records)} records`
              }.`
            : 'No upload has been imported yet.'}
        </Text>
        {uploads.recentRejected.length > 0 && (
          <Table aria-label="Recent rejected uploads" caption="Recently rejected">
            <TableHead>
              <TableCell>File</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Reason</TableCell>
            </TableHead>
            <TableBody>
              {uploads.recentRejected.map((upload) => (
                <TableRow key={upload.id}>
                  <TableCell label="File" tone="mono">
                    {upload.fileName}
                  </TableCell>
                  <TableCell label="Uploaded">{when(upload.uploadedAt)}</TableCell>
                  <TableCell label="Reason">{upload.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </SettingsSection>
  );
}

/** One row per provider and method, with the last day's and the last week's counts. */
function providerRows(lastDay: Providers, lastWeek: Providers) {
  const key = (p: Providers[number]) => `${p.provider} ${p.method ?? ''}`;
  const day = new Map(lastDay.map((p) => [key(p), p]));
  return lastWeek.map((week) => ({ week, day: day.get(key(week)) }));
}

function SignIns({ signIns }: Pick<Dashboard, 'signIns'>) {
  const rows = providerRows(signIns.lastDay, signIns.lastWeek);
  return (
    <SettingsSection
      title="Sign-ins"
      description="Successes and failures by provider, partner sign-ins by method (a login link or OIDC)."
    >
      <Stack gap={3}>
        {signIns.failureJump && (
          <Alert tone="warning" title="Sign-in failures jumped">
            {count(signIns.failuresLastDay)} failed sign-ins in the last 24 hours, against a daily
            average of {signIns.dailyFailuresBefore} in the 6 days before.
          </Alert>
        )}
        {rows.length === 0 ? (
          <Text tone="muted">No sign-ins in the last 7 days.</Text>
        ) : (
          <Table aria-label="Sign-ins by provider">
            <TableHead>
              <TableCell>Provider</TableCell>
              <TableCell numeric>Signed in, 24 h</TableCell>
              <TableCell numeric>Failed, 24 h</TableCell>
              <TableCell numeric>Signed in, 7 d</TableCell>
              <TableCell numeric>Failed, 7 d</TableCell>
            </TableHead>
            <TableBody>
              {rows.map(({ week, day }) => (
                <TableRow key={`${week.provider} ${week.method ?? ''}`}>
                  <TableCell label="Provider" tone="mono">
                    {week.provider}
                    {week.method ? ` (${week.method})` : ''}
                  </TableCell>
                  <TableCell label="Signed in, 24 h" numeric>
                    {count(day?.successes ?? 0)}
                  </TableCell>
                  <TableCell label="Failed, 24 h" numeric>
                    {count(day?.failures ?? 0)}
                  </TableCell>
                  <TableCell label="Signed in, 7 d" numeric>
                    {count(week.successes)}
                  </TableCell>
                  <TableCell label="Failed, 7 d" numeric>
                    {count(week.failures)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {signIns.topFailureReasonsLastWeek.length > 0 && (
          <Table aria-label="Top failure reasons" caption="Top failure reasons (7 days)">
            <TableHead>
              <TableCell>Provider</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell numeric>Failures</TableCell>
            </TableHead>
            <TableBody>
              {signIns.topFailureReasonsLastWeek.map((reason) => (
                <TableRow key={`${reason.provider} ${reason.reason}`}>
                  <TableCell label="Provider" tone="mono">
                    {reason.provider}
                  </TableCell>
                  <TableCell label="Reason">{reason.reason}</TableCell>
                  <TableCell label="Failures" numeric>
                    {count(reason.count)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Table aria-label="Partner SSO steps" caption="Partner SSO steps">
          <TableHead>
            <TableCell>Step</TableCell>
            <TableCell numeric>24 h</TableCell>
            <TableCell numeric>7 d</TableCell>
          </TableHead>
          <TableBody>
            {signIns.partnerSteps.map((step) => (
              <TableRow key={step.action}>
                <TableCell label="Step" tone="mono">
                  {step.action}
                </TableCell>
                <TableCell label="24 h" numeric>
                  {count(step.lastDay)}
                </TableCell>
                <TableCell label="7 d" numeric>
                  {count(step.lastWeek)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </SettingsSection>
  );
}

function Usage({ usage }: Pick<Dashboard, 'usage'>) {
  const lastWeek = usage.weekly[usage.weekly.length - 1];
  return (
    <SettingsSection title="Usage" description="Who signs in, and how often pay is viewed.">
      <Stack gap={3}>
        <Chart
          type="bar"
          title="People signing in per day"
          // Month/day, so 14 labels fit: "10/16".
          categories={usage.daily.map((day) => day.day.slice(5).replace('-', '/'))}
          series={[{ name: 'People', data: usage.daily.map((day) => day.users) }]}
          valueFormatter={(value) => count(value)}
        />
        <StatStrip>
          <StatCard
            label="This week"
            value={count(lastWeek?.users ?? 0)}
            hint="People signing in"
          />
          <StatCard
            label="Pay viewed"
            value={count(usage.payViewedLastWeek)}
            hint={`7 days; ${count(usage.payViewedLastDay)} in 24 hours`}
          />
          <StatCard label="Enabled users" value={count(usage.enabledUsers)} />
          <StatCard
            label="Never signed in"
            value={count(usage.neverSignedIn)}
            hint="Enabled users"
          />
          <StatCard label="Disabled users" value={count(usage.disabledUsers)} />
        </StatStrip>
      </Stack>
    </SettingsSection>
  );
}

const ACTIONS: Record<string, string> = {
  'impersonation.start': 'Started viewing as',
  'impersonation.stop': 'Stopped viewing as',
  'user.roles_change': 'Changed roles',
  'user.create_admin': 'Made an admin (CLI)',
  'user.grant_operator': 'Granted operator (CLI)',
  'user.revoke_operator': 'Revoked operator (CLI)',
  'settings.update': 'Changed settings',
};

function detail(change: Change): string {
  const details = change.details as Record<string, unknown>;
  if (change.action === 'user.roles_change') {
    const list = (value: unknown) => (Array.isArray(value) ? value.join(', ') : '');
    const removed =
      typeof details.operatorRemoved === 'string'
        ? ` (operator removed: ${details.operatorRemoved})`
        : '';
    return `${list(details.from)} → ${list(details.to)}${removed}`;
  }
  if (change.action === 'impersonation.stop' && typeof details.reason === 'string') {
    return `Reason: ${details.reason}`;
  }
  if (change.action === 'settings.update' && Array.isArray(details.changed)) {
    return details.changed.join(', ');
  }
  return '';
}

function SensitiveChanges({ changes }: { changes: Change[] }) {
  return (
    <SettingsSection
      title="Sensitive changes"
      description="Viewing as someone, role changes, admins and operators made on the host, and settings."
    >
      {changes.length === 0 ? (
        <Text tone="muted">None recorded.</Text>
      ) : (
        <Table aria-label="Sensitive changes">
          <TableHead>
            <TableCell>When</TableCell>
            <TableCell>Change</TableCell>
            <TableCell>By</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Details</TableCell>
          </TableHead>
          <TableBody>
            {changes.map((change) => (
              <TableRow key={change.id}>
                <TableCell label="When">{when(change.at)}</TableCell>
                <TableCell label="Change">{ACTIONS[change.action] ?? change.action}</TableCell>
                <TableCell label="By">{change.realUser?.name ?? 'The host'}</TableCell>
                <TableCell label="User">{change.targetUser?.name ?? '—'}</TableCell>
                <TableCell label="Details">{detail(change) || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SettingsSection>
  );
}

/** The operator dashboard (pc-wvi): one tenant's health, sign-ins, usage and changes. */
export function OperatorView() {
  const { data, error, refetch } = useOperatorDashboard();
  return (
    <PageLayout page="operator">
      {error && !data ? (
        <Alert
          tone="danger"
          title="The dashboard couldn’t be loaded"
          action={
            <Button size="sm" variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Alert>
      ) : !data ? (
        <SkeletonText lines={6} />
      ) : (
        <Stack gap={5}>
          <Text size="meta" tone="muted">
            As of {when(data.generatedAt)}. Refreshes every 30 seconds.
          </Text>
          <Version version={data.version} />
          <Worker worker={data.worker} jobs={data.jobs} />
          <Errors errors={data.errors} />
          <Jobs jobs={data.jobs} />
          <Uploads uploads={data.uploads} />
          <SignIns signIns={data.signIns} />
          <Usage usage={data.usage} />
          <SensitiveChanges changes={data.sensitiveChanges} />
        </Stack>
      )}
    </PageLayout>
  );
}
